/**
 * Shared dependencies
 */
import { CUSTOM_DATA_EVENTS } from '../shared/constants/events';
import type {
	SaveRecipePayload,
	SaveRecipeCallbackEvent,
	RemoveRecipePayload,
	RemoveRecipeCallbackEvent,
	InitialRecipeDataLoadedPayload,
	InitialRecipeDataLoadedCallbackEvent,
	FetchRecipesPayload,
	FetchRecipesCallbackEvent,
} from '../shared/types/recipe';
import type { CustomEventCallback } from '../shared/types/events';

/**
 * Internal dependencies
 */
import { BaseEvent } from './BaseEvent';

/**
 * Event handler for recipe folder-specific events.
 * Extends the BaseEvent to provide methods for
 * listening to and dispatching recipe folder-related events.
 */
export class RecipeEvent extends BaseEvent {
	/**
	 * Listens for the recipe saved to list event.
	 * @param {SaveRecipeCallbackEvent} callback - Function to handle the event. Receives the full CustomEvent.
	 * @return {() => void} A function to unsubscribe the listener.
	 */
	onSave(callback: SaveRecipeCallbackEvent): () => void {
		return this.listen(CUSTOM_DATA_EVENTS.RECIPE_SAVED_TO_LIST, callback);
	}

	/**
	 * Dispatches the recipe saved to list event.
	 * @param {SaveRecipePayload} payload - The data to send with the event.
	 */
	save(payload: SaveRecipePayload) {
		this.dispatch(CUSTOM_DATA_EVENTS.RECIPE_SAVED_TO_LIST, payload);
	}

	/**
	 * Listens for the remove recipe event.
	 * @param {RemoveRecipeCallbackEvent} callback - Function to handle the event. Receives the full CustomEvent.
	 * @return {() => void} A function to unsubscribe the listener.
	 */
	onRemove(callback: RemoveRecipeCallbackEvent): () => void {
		return this.listen(CUSTOM_DATA_EVENTS.RECIPE_REMOVED_FROM_LIST, callback);
	}

	/**
	 * Dispatches the remove recipe event.
	 * @param {RemoveRecipePayload} payload - The data to send with the event.
	 */
	remove(payload: RemoveRecipePayload) {
		this.dispatch(CUSTOM_DATA_EVENTS.RECIPE_REMOVED_FROM_LIST, payload);
	}

	/**
	 * Listens for the recipe moved in list event.
	 * @param {CustomEventCallback} callback - Function to handle the event. Receives the full CustomEvent.
	 * @return {() => void} A function to unsubscribe the listener.
	 */
	onMove(callback: CustomEventCallback): () => void {
		return this.listen(CUSTOM_DATA_EVENTS.RECIPE_MOVED_IN_LIST, callback);
	}

	/**
	 * Dispatches the recipe moved in list event.
	 * @param {any} payload - The data to send with the event.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	move(payload: any) {
		this.dispatch(CUSTOM_DATA_EVENTS.RECIPE_MOVED_IN_LIST, payload);
	}

	/**
	 * Listens for the initial recipe data loaded event.
	 * @param {InitialRecipeDataLoadedCallbackEvent} callback - Function to handle the event. Receives the full CustomEvent.
	 * @return {() => void} A function to unsubscribe the listener.
	 */
	onInitialDataLoaded(callback: InitialRecipeDataLoadedCallbackEvent): () => void {
		return this.listen(CUSTOM_DATA_EVENTS.RECIPE_INITIAL_DATA_LOADED, callback);
	}

	/**
	 * Dispatches the initial recipe data loaded event.
	 * @param {InitialRecipeDataLoadedPayload} payload - The data to send with the event.
	 */
	initialDataLoaded(payload: InitialRecipeDataLoadedPayload) {
		this.dispatch(CUSTOM_DATA_EVENTS.RECIPE_INITIAL_DATA_LOADED, payload);
	}

	/**
	 * Listens for the recipe fetched event.
	 * @param {FetchRecipesCallbackEvent} callback - Function to handle the event. Receives the full CustomEvent.
	 * @return {() => void} A function to unsubscribe the listener.
	 */
	onFetch(callback: FetchRecipesCallbackEvent): () => void {
		return this.listen(CUSTOM_DATA_EVENTS.RECIPE_FETCHED, callback);
	}

	/**
	 * Dispatches the 'RECIPE_FETCHED' event.
	 * @param {FetchRecipesPayload} payload - The data to send with the event.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	fetch(payload: FetchRecipesPayload) {
		this.dispatch(CUSTOM_DATA_EVENTS.RECIPE_FETCHED, payload);
	}
}
